/**
 * Kepala rel layar log, dalam dua keadaan, untuk kedua tepinya.
 *
 * Rel kanan memuat empat tab — Parameter, Track, Curves, Results — dan rel kiri
 * memuat pohon proyek; keduanya sampai kini selalu ada, bersama-sama sekitar
 * separuh lebar layar laptop, diambil terus-menerus oleh panel yang hanya
 * disentuh ketika sesuatu memang perlu disetel atau dicari. Membaca sebuah
 * kurva tidak menuntut satu pun kotak parameter terlihat, dan tidak pula
 * menuntut daftar sumur.
 *
 * Yang disediakan karena itu bukan lebar lain melainkan keadaan kedua: rel yang
 * terlipat menjadi pita selebar tombolnya sendiri. Pita itu tetap sebuah rel,
 * bukan sebuah tepi — namanya tetap terbaca menurun, yang sedang aktif tetap
 * ditandai, dan menekan salah satunya membuka rel pada tab tersebut. Rel yang
 * terlipat menjadi garis polos akan menyembunyikan tab mana yang sedang dipakai
 * sekaligus jalan kembali kepadanya.
 *
 * Semuanya di sini, bukan di layarnya, sebab rel kanan membaca satu daftar tab
 * yang sama pada kedua keadaannya — dua daftar yang disalin adalah tempat tab
 * kelima nanti terlupakan di salah satunya — dan sebab arah panah tombol
 * lipatnya adalah satu perkara yang sama pada keempat tempat ia dicetak.
 */
import { Icon } from "./icons";
import { IconButton } from "./ui";

export interface RailTabItem<T extends string> {
  key: T;
  label: string;
}

/** Tepi tempat rel itu duduk; dari situlah arah lipatnya dibaca. */
export type RailSide = "left" | "right";

/** Selebar tombol lipatnya, dan tidak lebih: pita ini ada untuk menghemat lebar. */
export const RAIL_STRIP_W = 30;

/**
 * Tombol lipat sebuah rel, panahnya menunjuk ke mana relnya akan pergi.
 *
 * Satu tombol untuk keempat tempat ia dicetak — terbuka dan terlipat, kiri dan
 * kanan — sebab arahnya adalah satu perkara yang sama, dan sebuah panah yang
 * menunjuk ke arah yang keliru pada satu di antaranya menjanjikan panel yang
 * muncul di seberang tempat ia sebenarnya muncul.
 */
export function RailCollapseButton({
  side,
  collapsed,
  label,
  onClick,
  size = 22,
}: {
  side: RailSide;
  /** Keadaan rel sekarang; panahnya menunjuk ke keadaan berikutnya. */
  collapsed: boolean;
  label: string;
  onClick: () => void;
  size?: number;
}) {
  // `Icon.back` menunjuk ke kiri. Rel kanan yang terbuka melipat ke kanan, rel
  // kiri yang terlipat membuka ke kanan; keduanya itulah panah yang dibalik.
  const rightward = collapsed === (side === "left");
  return (
    <IconButton title={label} size={size} onClick={onClick}>
      <span style={{ display: "flex", transform: rightward ? "rotate(180deg)" : undefined }}>
        {Icon.back({ size: 12 })}
      </span>
    </IconButton>
  );
}

/**
 * Deretan tab mendatar di kepala rel yang terbuka, berikut tombol lipatnya.
 *
 * Tombolnya duduk di sini dan bukan di pemisah yang bisa diseret: pemisah itu
 * sudah memikul pengubahan lebar, dan sebuah pegangan yang menyeret sekaligus
 * melipat adalah pegangan yang melipat setiap kali seretannya meleset.
 */
export function RailTabBar<T extends string>({
  tabs,
  active,
  onSelect,
  onCollapse,
  collapseLabel,
  side = "right",
}: {
  tabs: RailTabItem<T>[];
  active: T;
  onSelect: (key: T) => void;
  onCollapse: () => void;
  collapseLabel: string;
  side?: RailSide;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid var(--line)",
        flex: "none",
      }}
    >
      {tabs.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          aria-current={active === key}
          onClick={() => onSelect(key)}
          style={{
            flex: 1,
            minWidth: 0,
            padding: "8px 0",
            border: "none",
            borderBottom: `2px solid ${active === key ? "var(--accent)" : "transparent"}`,
            background: "transparent",
            color: active === key ? "var(--ink)" : "var(--ink-5)",
            font: `${active === key ? 600 : 500} 10.5px var(--font-sans)`,
            cursor: "pointer",
          }}
        >
          {label}
        </button>
      ))}
      <div style={{ flex: "none", padding: "0 5px 0 3px" }}>
        <RailCollapseButton
          side={side}
          collapsed={false}
          label={collapseLabel}
          onClick={onCollapse}
        />
      </div>
    </div>
  );
}

/**
 * Rel yang terlipat: sebuah pita yang masih bisa ditekan.
 *
 * Nama tabnya ditulis menurun dan utuh, bukan disingkat — "Param" dan "Res"
 * adalah dua kata yang tidak ada di layar mana pun, jadi pita yang memuatnya
 * menuntut penggunanya menerka kata yang sebenarnya sudah ia kenal.
 *
 * Rel kiri hanya memuat satu nama; pita itu tetap dicetak dari daftar yang
 * sama, sebab yang membedakannya dari sebuah tepi bukanlah banyaknya nama
 * melainkan bahwa namanya terbaca dan bisa ditekan.
 */
export function RailStrip<T extends string>({
  tabs,
  active,
  onSelect,
  expandLabel,
  side = "right",
}: {
  tabs: RailTabItem<T>[];
  active: T;
  onSelect: (key: T) => void;
  expandLabel: string;
  side?: RailSide;
}) {
  // Garis pemisah dan penanda tab sama-sama menghadap lembar log. Pada pita
  // kiri, penanda yang tetap digambar di kiri berdiri di tepi jendela — tempat
  // yang dilihat terakhir, kalau sempat terlihat.
  const inward = side === "right" ? "borderLeft" : "borderRight";
  return (
    <aside
      style={{
        width: RAIL_STRIP_W,
        flex: "none",
        background: "var(--rail)",
        [inward]: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        paddingTop: 6,
      }}
    >
      {/* Namanya menyebutkan arah geraknya — "Tampilkan" di sini,
          "Sembunyikan" di kepala rel yang terbuka — sebab sebuah tombol lipat
          yang bernama sama pada kedua keadaannya tidak mengatakan apa yang
          akan terjadi ketika ia ditekan. */}
      <RailCollapseButton
        side={side}
        collapsed
        label={expandLabel}
        size={24}
        onClick={() => onSelect(active)}
      />
      {tabs.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          title={`Open ${label}`}
          aria-current={active === key}
          onClick={() => onSelect(key)}
          style={{
            writingMode: "vertical-rl",
            padding: "10px 0",
            border: "none",
            [inward]: `2px solid ${active === key ? "var(--accent)" : "transparent"}`,
            background: "transparent",
            color: active === key ? "var(--ink)" : "var(--ink-5)",
            font: `${active === key ? 600 : 500} 10.5px var(--font-sans)`,
            letterSpacing: ".04em",
            cursor: "pointer",
          }}
        >
          {label}
        </button>
      ))}
    </aside>
  );
}
